import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useFieldArray } from "react-hook-form";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import InputText from "core/inputs/input-text/InputText";
import { Typography } from "core/typography/Typography";
import { Controller } from "utils/Controller";
import { DELIVERY_NOTE_COLUMN_TYPES } from "constants/constants";
import {
  DELIVERY_NOTE_COLUMN_BADGES,
  DELIVERY_NOTE_DEFAULT_COLUMN_NAMES,
  DELIVERY_NOTE_FORM_LABELS,
  DELIVERY_NOTE_FORM_PLACEHOLDERS,
} from "constants/forms";

const COLUMNS_FIELD = "columns";

const columnFieldName = (index, field) => `${COLUMNS_FIELD}.${index}.${field}`;

const ColumnsBlock = ({
  control,
  register,
  getValues,
  setValue,
  currencySymbol,
}) => {
  const intl = useIntl();
  const previousSymbolRef = useRef(currencySymbol);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: COLUMNS_FIELD,
  });

  const buildUnitPriceName = useCallback(
    (symbol) =>
      intl.formatMessage(DELIVERY_NOTE_DEFAULT_COLUMN_NAMES.unit_price, {
        currency: symbol,
      }),
    [intl]
  );

  useEffect(() => {
    const previousSymbol = previousSymbolRef.current;
    if (previousSymbol === currencySymbol) return;
    previousSymbolRef.current = currencySymbol;
    const previousName = buildUnitPriceName(previousSymbol);
    const nextName = buildUnitPriceName(currencySymbol);
    (getValues(COLUMNS_FIELD) || []).forEach((column, index) => {
      if (
        column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED &&
        column.name_b === previousName
      ) {
        setValue(columnFieldName(index, "name_b"), nextName);
      }
    });
  }, [currencySymbol, buildUnitPriceName, getValues, setValue]);

  const addSimpleColumn = () =>
    append(
      {
        type: DELIVERY_NOTE_COLUMN_TYPES.SIMPLE,
        name: intl.formatMessage(DELIVERY_NOTE_DEFAULT_COLUMN_NAMES.simple),
      },
      { shouldFocus: false }
    );

  const addLinkedColumn = () =>
    append(
      {
        type: DELIVERY_NOTE_COLUMN_TYPES.LINKED,
        name_a: intl.formatMessage(DELIVERY_NOTE_DEFAULT_COLUMN_NAMES.quantity),
        name_b: buildUnitPriceName(currencySymbol),
      },
      { shouldFocus: false }
    );

  const moveColumn = (index, offset) => move(index, index + offset);

  const renderMoveIcon = ({ index, offset }) => {
    const target = index + offset;
    const isDisabled = target < 0 || target >= fields.length;
    return (
      <span
        className={`delivery-note-columns__move${
          isDisabled ? " delivery-note-columns__move--disabled" : ""
        }`}
      >
        <Icon
          icon={offset < 0 ? "arrow-up-2" : "arrow-down-2"}
          color="violet"
          width={14}
          height={14}
          disabled={isDisabled}
          onClick={isDisabled ? undefined : () => moveColumn(index, offset)}
        />
      </span>
    );
  };

  const renderColumnField = ({ index, field, label }) => (
    <div className="cell-padding">
      <Controller
        control={control}
        register={register}
        name={columnFieldName(index, field)}
        render={(props) => (
          <InputText
            id={`column-${index}-${field}`}
            label={label}
            placeholder={DELIVERY_NOTE_FORM_PLACEHOLDERS.column_name}
            {...props}
          />
        )}
      />
    </div>
  );

  return (
    <div className="delivery-note-form__section">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="delivery_note_columns_block"
            defaultMessage="Colonnes"
          />
        }
      />
      <Typography
        className="delivery-note-form__notice delivery-note-form__notice--inline"
        type="p"
        label={
          <FormattedMessage
            id="delivery_note_columns_hint"
            defaultMessage="Les colonnes liées se multiplient par paire, la colonne Montant total est ajoutée automatiquement."
          />
        }
      />
      <ul className="delivery-note-columns">
        {fields.map((column, index) => {
          const isLinked = column.type === DELIVERY_NOTE_COLUMN_TYPES.LINKED;
          return (
            <li
              className={`delivery-note-columns__card${
                isLinked ? " delivery-note-columns__card--linked" : ""
              }`}
              key={column.id}
            >
              <div className="delivery-note-columns__header">
                <Typography
                  className="delivery-note-columns__type"
                  type="span"
                  label={intl.formatMessage(
                    isLinked
                      ? DELIVERY_NOTE_COLUMN_BADGES.linked
                      : DELIVERY_NOTE_COLUMN_BADGES.simple
                  )}
                />
                <div className="delivery-note-columns__buttons">
                  {renderMoveIcon({ index, offset: -1 })}
                  {renderMoveIcon({ index, offset: 1 })}
                  <Icon
                    icon="trash-2"
                    color="red"
                    className="delivery-note-columns__remove"
                    onClick={() => remove(index)}
                  />
                </div>
              </div>
              <div className="line-1">
                {isLinked ? (
                  <>
                    {renderColumnField({
                      index,
                      field: "name_a",
                      label: DELIVERY_NOTE_FORM_LABELS.column_a,
                    })}
                    <div className="cell-padding delivery-note-columns__operator">
                      <Typography
                        className="text-3 violet"
                        type="span"
                        label="×"
                      />
                    </div>
                    {renderColumnField({
                      index,
                      field: "name_b",
                      label: DELIVERY_NOTE_FORM_LABELS.column_b,
                    })}
                  </>
                ) : (
                  renderColumnField({
                    index,
                    field: "name",
                    label: DELIVERY_NOTE_FORM_LABELS.column_name,
                  })
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="delivery-note-columns__actions">
        <Button
          type="button"
          className="btn-small btn-outline"
          onClick={addSimpleColumn}
          id="delivery-note-add-simple-column"
        >
          <FormattedMessage {...DELIVERY_NOTE_COLUMN_BADGES.simple} />
        </Button>
        <Button
          type="button"
          color="btn-small gray-700"
          onClick={addLinkedColumn}
          id="delivery-note-add-linked-column"
        >
          <FormattedMessage {...DELIVERY_NOTE_COLUMN_BADGES.linked} />
        </Button>
      </div>
    </div>
  );
};

ColumnsBlock.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
};

export default ColumnsBlock;
